import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { setWhiteboards } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Eraser, Trash2, Plus, Save, X, ChevronLeft, ChevronRight, Image as ImageIcon, Copy, RotateCw } from "lucide-react";

const PEN_COLORS = ["#111827", "#EF4444", "#3B82F6", "#10B981", "#F59E0B", "#A855F7"];
const THEMES = [
  { id: "plain", label: "Plain" },
  { id: "grid", label: "Grid" },
  { id: "lined", label: "Lined" },
  { id: "dotted", label: "Dots" },
  { id: "stave", label: "Music" },
  { id: "ruler", label: "cm Ruler" },
];
function themeStyle(theme) {
  const line = "#c3cee0";
  if (theme === "grid") return { backgroundColor: "#fff", backgroundImage: `linear-gradient(${line} 1px, transparent 1px), linear-gradient(90deg, ${line} 1px, transparent 1px)`, backgroundSize: "28px 28px" };
  if (theme === "lined") return { backgroundColor: "#fff", backgroundImage: `linear-gradient(${line} 1px, transparent 1px)`, backgroundSize: "100% 38px" };
  if (theme === "dotted") return { backgroundColor: "#fff", backgroundImage: `radial-gradient(${line} 1.6px, transparent 1.7px)`, backgroundSize: "26px 26px" };
  if (theme === "stave") return {
    backgroundColor: "#fff",
    backgroundImage: `linear-gradient(to bottom, transparent 0 12px, #334155 12px 14px, transparent 14px 22px, #334155 22px 24px, transparent 24px 32px, #334155 32px 34px, transparent 34px 42px, #334155 42px 44px, transparent 44px 52px, #334155 52px 54px, transparent 54px 84px)`,
    backgroundSize: "100% 84px",
  };
  if (theme === "ruler") return {
    backgroundColor: "#fff",
    backgroundImage: `linear-gradient(#8aa0c4 1px, transparent 1px), linear-gradient(90deg, #8aa0c4 1px, transparent 1px), linear-gradient(#e0e7f3 1px, transparent 1px), linear-gradient(90deg, #e0e7f3 1px, transparent 1px)`,
    backgroundSize: "37.8px 37.8px, 37.8px 37.8px, 3.78px 3.78px, 3.78px 3.78px",
  };
  return { backgroundColor: "#fff" };
}

// Full-screen themed whiteboard with per-class saved boards (tabs). Pen / eraser drawing.
export default function Whiteboard({ open, onClose, room, onRoom, accent = "#3B82F6" }) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawing = useRef(false);
  const imageInputRef = useRef(null);
  const [boards, setBoards] = useState([]);
  const [active, setActive] = useState(0);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState(PEN_COLORS[0]);
  const [saving, setSaving] = useState(false);
  const [editingTab, setEditingTab] = useState(null);
  const skipBlurRef = useRef(false);
  const [pendingImg, setPendingImg] = useState(null);
  const pendingImgElRef = useRef(null);
  const [confirmClear, setConfirmClear] = useState(false);

  // Load boards from room when opened.
  useEffect(() => {
    if (!open) return;
    const wbs = (room.whiteboards && room.whiteboards.length) ? room.whiteboards : [{ id: crypto.randomUUID(), name: "Board 1", dataUrl: "" }];
    setBoards(wbs);
    setActive(0);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const setupCanvas = (dataUrl) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctxRef.current = ctx;
    if (dataUrl) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = dataUrl;
    }
  };

  // (Re)draw when active board changes / opens.
  useEffect(() => {
    if (!open || !boards.length) return;
    const t = setTimeout(() => setupCanvas(boards[active]?.dataUrl), 60);
    return () => clearTimeout(t);
  }, [open, active, boards.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - r.left, y: p.clientY - r.top };
  };
  const startDraw = (e) => {
    drawing.current = true;
    const ctx = ctxRef.current;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const moveDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ctxRef.current;
    const { x, y } = pos(e);
    if (tool === "eraser") { ctx.globalCompositeOperation = "destination-out"; ctx.lineWidth = 34; }
    else { ctx.globalCompositeOperation = "source-over"; ctx.strokeStyle = color; ctx.lineWidth = 4; }
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const endDraw = () => { drawing.current = false; };

  const clearBoard = () => setupCanvas("");

  // Drop / pick a worksheet or diagram. It becomes a movable + resizable overlay that the
  // teacher positions, then "Place" commits it onto the board so they can annotate over it.
  const startPendingImage = (src) => {
    const img = new Image();
    img.onload = () => {
      const c = canvasRef.current;
      if (!c) return;
      pendingImgElRef.current = img;
      const ratio = img.width / img.height;
      let w = c.width * 0.55, h = w / ratio;
      if (h > c.height * 0.8) { h = c.height * 0.8; w = h * ratio; }
      setPendingImg({ src, x: (c.width - w) / 2, y: (c.height - h) / 2, w, h, ratio, angle: 0 });
    };
    img.src = src;
  };
  const onImageFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const r = new FileReader();
    r.onload = () => startPendingImage(r.result);
    r.readAsDataURL(file);
  };
  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) onImageFile(f);
  };
  const beginDrag = (mode, e) => {
    e.preventDefault();
    e.stopPropagation();
    const start = { px: e.clientX, py: e.clientY, x: pendingImg.x, y: pendingImg.y, w: pendingImg.w, ratio: pendingImg.ratio };
    const onMove = (ev) => {
      const dx = ev.clientX - start.px, dy = ev.clientY - start.py;
      setPendingImg((p) => {
        if (!p) return p;
        if (mode === "move") return { ...p, x: start.x + dx, y: start.y + dy };
        const nw = Math.max(40, start.w + dx);
        return { ...p, w: nw, h: nw / start.ratio };
      });
    };
    const onUp = () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  const placePending = () => {
    const img = pendingImgElRef.current, ctx = ctxRef.current;
    if (img && ctx && pendingImg) {
      ctx.globalCompositeOperation = "source-over";
      const { x, y, w, h, angle } = pendingImg;
      if (angle) {
        ctx.save();
        ctx.translate(x + w / 2, y + h / 2);
        ctx.rotate(angle);
        ctx.drawImage(img, -w / 2, -h / 2, w, h);
        ctx.restore();
      } else {
        ctx.drawImage(img, x, y, w, h);
      }
    }
    setPendingImg(null);
    pendingImgElRef.current = null;
  };
  const cancelPending = () => { setPendingImg(null); pendingImgElRef.current = null; };
  const beginRotate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const r = canvasRef.current.getBoundingClientRect();
    const cx = r.left + pendingImg.x + pendingImg.w / 2;
    const cy = r.top + pendingImg.y + pendingImg.h / 2;
    const onMove = (ev) => {
      const ang = Math.atan2(ev.clientY - cy, ev.clientX - cx) + Math.PI / 2;
      setPendingImg((p) => (p ? { ...p, angle: ang } : p));
    };
    const onUp = () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const captureActive = () => {
    const dataUrl = canvasRef.current?.toDataURL("image/png");
    return boards.map((b, i) => (i === active ? { ...b, dataUrl } : b));
  };

  const addBoard = () => {
    const snapped = captureActive();
    const nb = { id: crypto.randomUUID(), name: `Board ${snapped.length + 1}`, dataUrl: "" };
    setBoards([...snapped, nb]);
    setActive(snapped.length);
  };

  const switchBoard = (i) => {
    if (i === active) return;
    cancelPending();
    setBoards(captureActive());
    setActive(i);
  };

  const renameBoard = (i, name) => setBoards((bs) => bs.map((b, j) => (j === i ? { ...b, name: name.trim() || b.name } : b)));
  const duplicateBoard = () => {
    cancelPending();
    const snapped = captureActive();
    const src = snapped[active];
    const copy = { id: crypto.randomUUID(), name: `${src.name} copy`, dataUrl: src.dataUrl, theme: src.theme };
    setBoards([...snapped.slice(0, active + 1), copy, ...snapped.slice(active + 1)]);
    setActive(active + 1);
  };
  const moveBoard = (dir) => {
    const j = active + dir;
    if (j < 0 || j >= boards.length) return;
    cancelPending();
    const snapped = captureActive();
    const arr = [...snapped];
    [arr[active], arr[j]] = [arr[j], arr[active]];
    setBoards(arr);
    setActive(j);
  };

  const currentTheme = boards[active]?.theme || "plain";
  const setTheme = (t) => setBoards((bs) => bs.map((b, i) => (i === active ? { ...b, theme: t } : b)));

  const save = async () => {
    setSaving(true);
    try {
      const snapped = captureActive();
      setBoards(snapped);
      const d = await setWhiteboards(room.id, snapped);
      onRoom && onRoom(d);
      toast.success("Boards saved 🖍️");
    } catch {
      toast.error("Could not save boards");
    }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <div data-testid="whiteboard-overlay" className="fixed inset-0 z-[75] bg-[#0B0D1B]/90 backdrop-blur-sm p-2 sm:p-4 flex flex-col">
      <div className="rounded-2xl border-4 flex flex-col flex-1 overflow-hidden" style={{ borderColor: accent, boxShadow: `0 0 40px ${accent}66` }}>
        {/* Toolbar */}
        <div className="flex items-center gap-2 px-3 py-2 bg-[#0E1126] flex-wrap">
          <Button data-testid="wb-pen-btn" onClick={() => setTool("pen")} size="sm" variant={tool === "pen" ? "default" : "outline"} className={tool === "pen" ? "text-[#0B0D1B]" : "border-[#252B5C] bg-transparent text-white"} style={tool === "pen" ? { backgroundColor: accent } : {}}>
            <Pencil className="w-4 h-4 mr-1" /> Pen
          </Button>
          <Button data-testid="wb-eraser-btn" onClick={() => setTool("eraser")} size="sm" variant={tool === "eraser" ? "default" : "outline"} className={tool === "eraser" ? "text-[#0B0D1B]" : "border-[#252B5C] bg-transparent text-white"} style={tool === "eraser" ? { backgroundColor: accent } : {}}>
            <Eraser className="w-4 h-4 mr-1" /> Eraser
          </Button>
          <div className="flex items-center gap-1 ml-1" data-testid="wb-color-picker">
            {PEN_COLORS.map((c) => (
              <button key={c} data-testid={`wb-color-${c}`} onClick={() => { setColor(c); setTool("pen"); }} className={`w-6 h-6 rounded-full border-2 ${color === c && tool === "pen" ? "border-white scale-110" : "border-transparent"}`} style={{ backgroundColor: c }} />
            ))}
          </div>
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-[#252B5C]" data-testid="wb-theme-picker">
            {THEMES.map((t) => (
              <button key={t.id} data-testid={`wb-theme-${t.id}`} onClick={() => setTheme(t.id)} title={`${t.label} background`}
                className={`px-2 h-7 rounded-md text-xs font-heading border ${currentTheme === t.id ? "text-[#0B0D1B]" : "border-[#252B5C] bg-[#141738] text-slate-300"}`}
                style={currentTheme === t.id ? { backgroundColor: accent, borderColor: accent } : {}}>
                {t.label}
              </button>
            ))}
          </div>
          <input ref={imageInputRef} data-testid="wb-image-input" type="file" accept="image/*" className="hidden" onChange={(e) => { onImageFile(e.target.files[0]); e.target.value = ""; }} />
          <Button data-testid="wb-image-btn" onClick={() => imageInputRef.current?.click()} size="sm" variant="outline" className="border-[#252B5C] bg-transparent text-white ml-auto" title="Add a worksheet / diagram to annotate (or drag one onto the board)">
            <ImageIcon className="w-4 h-4 mr-1" /> Image
          </Button>
          <Button data-testid="wb-clear-btn" onClick={() => setConfirmClear(true)} size="sm" variant="outline" className="border-[#252B5C] bg-transparent text-white">
            <Trash2 className="w-4 h-4 mr-1" /> Clear
          </Button>
          <Button data-testid="wb-save-btn" onClick={save} disabled={saving} size="sm" className="text-[#0B0D1B] font-heading font-bold" style={{ backgroundColor: accent }}>
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
          <Button data-testid="wb-close-btn" onClick={onClose} size="sm" variant="outline" className="border-[#252B5C] bg-transparent text-white">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-3 py-1.5 bg-[#0E1126] border-t border-[#252B5C] overflow-x-auto" data-testid="wb-tabs">
          {boards.map((b, i) => (
            i === active ? (
              <div key={b.id} data-testid={`wb-tab-${i}`} className="shrink-0 flex items-center gap-1 rounded-t-lg pl-1 pr-1.5 py-0.5 text-[#0B0D1B] font-heading font-semibold text-sm" style={{ backgroundColor: accent }}>
                <button data-testid={`wb-tab-move-left-${i}`} onClick={() => moveBoard(-1)} disabled={i === 0} title="Move left" className="disabled:opacity-30 hover:opacity-70">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {editingTab === i ? (
                  <input
                    data-testid={`wb-tab-input-${i}`}
                    autoFocus
                    defaultValue={b.name}
                    onBlur={(e) => { if (skipBlurRef.current) { skipBlurRef.current = false; return; } renameBoard(i, e.target.value); setEditingTab(null); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { renameBoard(i, e.target.value); setEditingTab(null); } if (e.key === "Escape") { skipBlurRef.current = true; setEditingTab(null); } }}
                    className="w-24 bg-white/80 rounded px-1 text-[#0B0D1B] text-sm outline-none"
                  />
                ) : (
                  <button data-testid={`wb-tab-label-${i}`} onDoubleClick={() => setEditingTab(i)} title="Double-click to rename" className="px-1 max-w-[140px] truncate">
                    {b.name}
                  </button>
                )}
                <button data-testid={`wb-tab-rename-${i}`} onClick={() => setEditingTab(i)} title="Rename" className="hover:opacity-70">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button data-testid={`wb-tab-duplicate-${i}`} onClick={duplicateBoard} title="Duplicate board (reuse as a template)" className="hover:opacity-70">
                  <Copy className="w-3.5 h-3.5" />
                </button>
                <button data-testid={`wb-tab-move-right-${i}`} onClick={() => moveBoard(1)} disabled={i === boards.length - 1} title="Move right" className="disabled:opacity-30 hover:opacity-70">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button key={b.id} data-testid={`wb-tab-${i}`} onClick={() => switchBoard(i)}
                className="shrink-0 px-3 py-1 rounded-t-lg text-sm font-heading font-semibold bg-[#141738] text-slate-300 max-w-[160px] truncate">
                {b.name}
              </button>
            )
          ))}
          <button data-testid="wb-add-board-btn" onClick={addBoard} className="shrink-0 px-2 py-1 rounded-lg text-slate-300 hover:text-white" title="New board">
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Canvas */}
        <div className="flex-1 relative overflow-hidden" data-testid="whiteboard-surface" style={themeStyle(currentTheme)} onDrop={onDrop} onDragOver={(e) => e.preventDefault()}>
          <canvas
            ref={canvasRef}
            data-testid="whiteboard-canvas"
            className={`absolute inset-0 w-full h-full touch-none cursor-crosshair ${pendingImg ? "pointer-events-none" : ""}`}
            onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
            onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}
          />
          {pendingImg && (
            <>
            <div data-testid="wb-pending-image" className="absolute" style={{ left: pendingImg.x, top: pendingImg.y, width: pendingImg.w, height: pendingImg.h, transform: `rotate(${pendingImg.angle || 0}rad)` }}>
              <img src={pendingImg.src} alt="" draggable={false} className="w-full h-full select-none pointer-events-none" style={{ boxShadow: `0 0 0 2px ${accent}`, opacity: 0.97 }} />
              <div data-testid="wb-image-move" onPointerDown={(e) => beginDrag("move", e)} className="absolute inset-0 cursor-move touch-none" title="Drag to move" />
              <div data-testid="wb-image-rotate" onPointerDown={beginRotate} className="absolute left-1/2 -top-9 -translate-x-1/2 w-6 h-6 rounded-full border-2 bg-white cursor-grab touch-none flex items-center justify-center" style={{ borderColor: accent }} title="Drag to rotate">
                <RotateCw className="w-3.5 h-3.5 text-[#0B0D1B]" />
              </div>
              <div data-testid="wb-image-resize" onPointerDown={(e) => beginDrag("resize", e)} className="absolute -bottom-2.5 -right-2.5 w-6 h-6 rounded-full border-2 bg-white cursor-nwse-resize touch-none" style={{ borderColor: accent }} title="Drag to resize" />
            </div>
            <div data-testid="wb-image-actions" className="absolute z-10 flex gap-1" style={{ left: pendingImg.x, top: Math.max(4, pendingImg.y - 44) }}>
              <button data-testid="wb-image-place" onClick={placePending} className="px-3 h-8 rounded-lg text-[#0B0D1B] text-sm font-heading font-bold shadow-lg" style={{ backgroundColor: accent }}>Place</button>
              <button data-testid="wb-image-cancel" onClick={cancelPending} className="px-3 h-8 rounded-lg bg-[#141738] border border-[#252B5C] text-white text-sm font-heading">Cancel</button>
            </div>
            </>
          )}
        </div>

        {confirmClear && (
          <div className="absolute inset-0 z-[80] flex items-center justify-center bg-black/50" data-testid="wb-clear-confirm">
            <div className="bg-[#141738] border border-[#252B5C] rounded-2xl p-5 max-w-xs text-center">
              <p className="font-heading font-bold text-white text-lg mb-1">Clear this board?</p>
              <p className="text-slate-400 text-sm mb-4">Your drawing on this board will be wiped and can't be undone.</p>
              <div className="flex gap-2 justify-center">
                <Button data-testid="wb-clear-confirm-yes" onClick={() => { clearBoard(); setConfirmClear(false); }} className="bg-red-500 hover:bg-red-600 text-white font-heading font-bold min-h-[44px]">Clear it</Button>
                <Button data-testid="wb-clear-confirm-no" onClick={() => setConfirmClear(false)} variant="outline" className="border-[#252B5C] bg-transparent text-white min-h-[44px]">Cancel</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
